import React, { useEffect, useState } from "react";
import { CepInput } from "./CepInput";
import { observer } from "mobx-react-lite";
import { store } from "../stores/Store";
import { formatBRL, validarCpf, validarCnpj } from "../utils";
import { useNavigate } from "react-router-dom";

export const CheckoutForm: React.FC = observer(() => {
  const { cartItems, cartTotal, confirmOrder } = store;

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [cep, setCep] = useState(store.checkoutCep);
  const [errors, setErrors] = useState<{
    nome?: string;
    email?: string;
    emailConfirm?: string;
    cpfCnpj?: string;
  }>({});

  const navigate = useNavigate();

  const [cepError, setCepError] = useState<string | undefined>(undefined);
  const [showAddressFields, setShowAddressFields] = useState(true);
  const [calculando, setCalculando] = useState(false);
  const [frete, setFrete] = useState<number | null>(store.checkoutFrete);
  const [envio, setEnvio] = useState<string>(store.checkoutEnvio || "SEDEX");
  const [address, setAddress] = useState({
    endereco: "",
    numero: "",
    cidade: "",
    estado: "",
    complemento: "",
  });
  const [pagamento, setPagamento] = useState("1"); // "1" for PIX by default
  const [processando, setProcessando] = useState(false);

  const podeFinalizer = frete !== null && !calculando;

  useEffect(() => {
    handleCalcularFrete();
  }, [envio]);

  const validate = () => {
    const newErrors: typeof errors = {};
    if (nome.trim().length < 5) {
      newErrors.nome = "O nome deve ter pelo menos 5 caracteres.";
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Digite um e-mail válido.";
    }
    const cpfCnpjDigits = cpfCnpj.replace(/\D/g, "");
    if (cpfCnpjDigits.length !== 11 && cpfCnpjDigits.length !== 14) {
      newErrors.cpfCnpj = "Digite um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.";
    }
    if (email !== emailConfirm) {
      newErrors.emailConfirm = "Os e-mails não coincidem.";
    } else if (!/\S+@\S+\.\S+/.test(emailConfirm)) {
      newErrors.emailConfirm = "Digite um e-mail válido.";
    }
    // Validação de CEP brasileiro (8 dígitos)
    if (!/^\d{8}$/.test(cep)) {
      setCepError("Digite um CEP válido (8 dígitos, apenas números).");
    } else {
      setCepError(undefined);
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !cepError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setProcessando(true);

    // Armazenar os dados do cliente na store antes de confirmar o pedido
    store.clienteData = {
      name: nome,
      email: email,
      cpf_cnpj: cpfCnpj,
      cep: cep,
      endereco: address.endereco,
      numero: address.numero,
      complemento: address.complemento,
      cidade: address.cidade,
      estado: address.estado,
    };

    // Define o ID da forma de envio
    const forma_envio_id = envio === "SEDEX" ? 1 : 2;
    const forma_pagamento_id = Number(pagamento);
    const pedido_numero = await confirmOrder(
      forma_envio_id,
      forma_pagamento_id,
      frete ?? 0
    );

    if (!pedido_numero) {
      setProcessando(false);
      return;
    }

    // Generate PIX cobrança
    const cobranca = await store.gerarCobrancaPix(pedido_numero);

    store.isCheckoutOpen = false;
    navigate("/pedido_detalhes", {
      replace: true,
      state: {
        orderId: pedido_numero,
        email,
        pixCopiaECola: cobranca?.pix_copia_e_cola || null,
        pixValor: cobranca?.valor || null,
      },
    });
  };

  const handleCalcularFrete = async () => {
    setCepError(undefined);
    setShowAddressFields(false);

    if (!/^\d{8}$/.test(cep)) {
      setCepError("Digite um CEP válido (8 dígitos, apenas números).");
      setFrete(null);
      return;
    }

    setCalculando(true);
    try {
      // Exemplo: ajuste conforme o retorno real do seu store
      const codProdutos = cartItems.map((item) => item.id);

      const result = await store.calculaFrete(codProdutos, cep, envio);
      setFrete(result?.custoFrete ?? 0);
      setShowAddressFields(true);
    } catch {
      setCepError("Erro ao calcular frete. Verifique o CEP.");
      setShowAddressFields(false);
      setFrete(null);
    }
    setCalculando(false);
  };

  if (processando) {
    return (
      <div className="container-fluid bg_branco">
        <div className="container ctn_default">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
              gap: "20px",
            }}
          >
            <div
              className="spinner"
              style={{
                width: 48,
                height: 48,
                border: "4px solid #e0e0e0",
                borderTop: "4px solid #333",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p className="titulo titulo_25 titulo_400 txt_preto">
              Processando seu pedido...
            </p>
            <p className="titulo_15" style={{ color: "#666" }}>
              Estamos gerando o PIX para pagamento. Aguarde um momento.
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid bg_branco">
      <div className="container ctn_default">
        <div className="row">
          <div className="col-md-8 col-md-push-2">
            <form onSubmit={handleSubmit}>
              <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
                <span className="material-symbols-outlined">shopping_cart</span>
                Dados do Comprador
              </h2>

              <div className="mg-bt_40">
                <label className="titulo_15 titulo_700">Seu Nome*</label>
                <input
                  type="text"
                  name="nome"
                  required
                  className="mg-bt_10"
                  value={nome}
                  minLength={5}
                  onChange={(e) => setNome(e.target.value)}
                />
                {errors.nome && (
                  <div style={{ color: "#dc3545", marginBottom: 8 }}>
                    {errors.nome}
                  </div>
                )}

                <label className="titulo_15 titulo_700">Seu E-mail*</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.email && (
                  <div style={{ color: "#dc3545", marginBottom: 8 }}>
                    {errors.email}
                  </div>
                )}

                <label className="titulo_15 titulo_700">
                  Confirme seu E-mail*
                </label>
                <input
                  type="email"
                  name="emailConfirm"
                  required
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                />
                {errors.emailConfirm && (
                  <div style={{ color: "#dc3545", marginBottom: 8 }}>
                    {errors.emailConfirm}
                  </div>
                )}

                <label className="titulo_15 titulo_400 text-center txt_cor-id-1 mg-bt_10 dsp_blc">
                  ↑ Responderemos por aqui
                </label>

                <label className="titulo_15 titulo_700">CPF ou CNPJ*</label>
                <input
                  type="text"
                  name="cpfCnpj"
                  required
                  value={cpfCnpj}
                  placeholder="000.000.000-00"
                  onChange={(e) => setCpfCnpj(e.target.value)}
                />
                {errors.cpfCnpj && (
                  <div style={{ color: "#dc3545", marginBottom: 8 }}>
                    {errors.cpfCnpj}
                  </div>
                )}
              </div>

              <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
                <span className="material-symbols-outlined">
                  local_shipping
                </span>
                Endereço de Entrega
              </h2>

              <div className="form_row row_3-1 mg-bt_10">
                <div>
                  <label className="titulo_15 titulo_700">CEP</label>
                  <CepInput
                    name="cep"
                    required
                    value={cep}
                    onChange={setCep}
                  />
                  {cepError && (
                    <div style={{ color: "#dc3545", marginBottom: 8 }}>
                      {cepError}
                    </div>
                  )}
                </div>
                <div className="ctn_bt">
                  <button
                    type="button"
                    className="bt bt_cor-id-3 bt_full"
                    onClick={handleCalcularFrete}
                    disabled={calculando}
                  >
                    {calculando ? "Calculando..." : "Recalcular frete"}
                  </button>
                </div>
              </div>

              {/* Endereço só aparece após calcular frete com sucesso */}
              {showAddressFields && (
                <>
                  <div className="form_row row_3-1 mg-bt_10">
                    <div>
                      <label className="titulo_15 titulo_700">Endereço</label>
                      <input
                        type="text"
                        name="endereco"
                        required
                        value={address.endereco}
                        onChange={(e) =>
                          setAddress({ ...address, endereco: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="titulo_15 titulo_700">Número</label>
                      <input
                        type="number"
                        name="numero"
                        required
                        value={address.numero}
                        onChange={(e) =>
                          setAddress({ ...address, numero: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="form_row row_3-1 mg-bt_10">
                    <div>
                      <label className="titulo_15 titulo_700">Cidade</label>
                      <input
                        type="text"
                        name="cidade"
                        required
                        value={address.cidade}
                        onChange={(e) =>
                          setAddress({ ...address, cidade: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="titulo_15 titulo_700">Estado</label>
                      <select
                        name="estado"
                        required
                        value={address.estado}
                        onChange={(e) =>
                          setAddress({ ...address, estado: e.target.value })
                        }
                      >
                        <option value="">Selecione</option>
                        {["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"].map(uf => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mg-bt_40">
                    <label className="titulo_15 titulo_700">Complemento</label>
                    <input
                      type="text"
                      name="complemento"
                      value={address.complemento}
                      onChange={(e) =>
                        setAddress({ ...address, complemento: e.target.value })
                      }
                    />
                  </div>

                  <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
                    <span className="material-symbols-outlined">
                      local_shipping
                    </span>
                    Forma de Envio
                  </h2>

                  <div className="ctn_radios mg-bt_20">
                    <div>
                      <input
                        type="radio"
                        id="correios-sedex"
                        name="envio"
                        value="SEDEX"
                        checked={envio === "SEDEX"}
                        onChange={() => setEnvio("SEDEX")}
                      />
                      <label
                        htmlFor="correios-sedex"
                        className="titulo_15 titulo_700"
                      >
                        Correios SEDEX
                      </label>
                    </div>
                    <div>
                      <input
                        type="radio"
                        id="correios-pac"
                        name="envio"
                        value="PAC"
                        checked={envio === "PAC"}
                        onChange={() => setEnvio("PAC")}
                      />
                      <label
                        htmlFor="correios-pac"
                        className="titulo_15 titulo_700"
                      >
                        Correios PAC
                      </label>
                    </div>
                  </div>
                </>
              )}

              <h2 className="titulo titulo_25 titulo_400 txt_preto mg-bt_20">
                <span className="material-symbols-outlined">shopping_bag</span>
                Forma de Pagamento
              </h2>

              <div className="ctn_radios mg-bt_20">
                <div>
                  <input
                    type="radio"
                    id="pix"
                    name="pagamento"
                    value="1"
                    checked={pagamento === "1"}
                    onChange={(e) => setPagamento(e.target.value)}
                  />
                  <label htmlFor="pix" className="titulo_15 titulo_700">
                    PIX
                  </label>
                </div>
              </div>

              <div className="ctn_value mg-bt_10">
                <div>
                  <p className="titulo_25">
                    <b>Frete:</b>
                  </p>
                </div>

                <div>
                  <p className="titulo_25">{formatBRL(frete ?? 0)}</p>
                </div>
              </div>

              <div className="ctn_value mg-bt_40">
                <div>
                  <p className="titulo_25">
                    <b>Total:</b>
                  </p>
                </div>

                <div>
                  <p className="titulo_25 txt_cor-id-1">
                    <b>{formatBRL(cartTotal + (frete ?? 0))}</b>
                  </p>
                </div>
              </div>

              <div className="ctn_bts">
                <div>
                  <a href="/" className="bt bt_big">
                    Continuar comprando
                  </a>
                </div>
                <div>
                  <button
                    type="submit"
                    className="bt bt_big"
                    style={{
                      width: "100%",
                      opacity: !podeFinalizer ? 0.5 : 1,
                      pointerEvents: !podeFinalizer ? "none" : undefined,
                      cursor: !podeFinalizer ? "not-allowed" : "pointer",
                    }}
                    disabled={!podeFinalizer}
                  >
                    Ir para Pagamento
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
});


